<svelte:options runes={false} />
<script>
  // TWAP tab (TASK-20260823-twap-strategies-tca AC 25, PR-C): the strategy's TWAP executions — headline
  // savings KPIs (client rollups over the parent list), one LIVE card per Working parent (progress ring, pace
  // bar with the ±2 % band, posture chip, ETA, Cancel with explicit confirm), the parent list (newest first,
  // opens the shared per-parent TwapReport in the SlideInPanel) and the savings-by-symbol table.
  //
  // Data flow: ONE writer (lesson 2026-08-18) — a scopedLoader captures (org, strategy) at send and drops a
  // response that lands after either moved. Refresh triggers: mount / (strategy, org) change, a `twap-progress`
  // SSE hint for THIS strategy (debounced; the payload is a watermark, never a source of numbers — AC 24),
  // and a 30 s backstop poll while anything is Working. Financial figures always come from the authoritative
  // list/detail reads. Paper vs live is unmistakable: the mode badge + every row's FeeRateSource badge.
  //
  // PR-C2 (D9) adds three regions that each load independently with their own empty/loading state:
  //   - cumulative savings over time (pure, from the list);
  //   - execution quality — posture mix + slice latency, rolled up from the scorecards of the newest finished
  //     parents (a finished parent's scorecard is immutable, so it is read ONCE per id and cached);
  //   - the day×hour maker-capture heatmap (GET /api/twap/heatmap for this strategy's requestId/source).
  // The live cards' posture chip and pace adherence now come off the list row itself (no detail reads).
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { createScopedLoader } from '../../../lib/scopedLoader.ts';
  import { addSseSubscriber } from '../../../lib/sseStore.ts';
  import { formatQuoteAmount } from '../../../lib/quoteCurrency.js';
  import { targetOf, isRealMoney, targetBadge } from '../../../lib/runContext.js';
  import { statusTone, isWorking, feeSourceBadge, STATUS_LABELS, fmtQuote, fmtPct, fmtBps, fmtQty, PACE_BAND_PCT } from '../../../lib/twapReport.js';
  import {
    tabKpis, bySymbol, liveParentsView, newestFirst, sortParents, filterParents, tickTouchesStrategy, STATUS_FILTERS,
    cumulativeSavingsSeries, postureMixRollup, latencyRollup, heatmapGrid,
  } from '../../../lib/twapTab.js';
  import SlideInPanel from '../SlideInPanel.svelte';
  import TwapReport from '../../shared/twap/TwapReport.svelte';
  import CumulativeSavingsChart from '../../shared/twap/CumulativeSavingsChart.svelte';
  import PostureMixBar from '../../shared/twap/PostureMixBar.svelte';
  import MakerCaptureHeatmap from '../../shared/twap/MakerCaptureHeatmap.svelte';
  import LatencyTile from '../../shared/twap/LatencyTile.svelte';

  export let strategy = null;
  export let status = null;
  export let isLive = false;

  $: strategyId = strategy?.strategyId ?? null; // the AutoTradeRequests id = the parents' RequestId
  $: isRunning = String(status) === 'Running';

  // ---- state (one writer: the scoped loader below) ----
  let parents = null;      // TwapParentSummary[] (null until the first read lands)
  let error = null;
  let loading = false;
  let inFlight = false;
  let queued = false;      // a hint arrived mid-flight → run once more when it lands
  let confirmCancelId = null; // the parent awaiting an explicit cancel confirm

  const PAGE_SIZE = 200;         // the API's max page; newest first (a capped read keeps the NEWEST window)

  const errText = (err) => {
    const msg = String(err?.message ?? err);
    return msg.includes('details: ') ? msg.slice(msg.indexOf('details: ') + 'details: '.length) : msg;
  };

  const loadParents = createScopedLoader({
    getOrgId: () => $selectedOrgId,
    getScope: () => [strategyId],
    load: async (orgId) => {
      const page = await api.getTwapParents({ requestId: strategyId, pageSize: PAGE_SIZE }, orgId);
      return Array.isArray(page?.items) ? page.items : [];
    },
    apply: (items) => {
      parents = items;
      error = null;
    },
    onError: (err) => { error = errText(err); },
  });

  async function refresh() {
    if (!strategyId) return;
    if (inFlight) { queued = true; return; }
    inFlight = true;
    loading = parents === null;
    try {
      await loadParents();
    } finally {
      loading = false;
      inFlight = false;
      if (queued) { queued = false; void refresh(); }
    }
    // The dependent regions refresh AFTER the list lands (their inputs are the list's finished ids).
    void refreshQuality();
    void refreshHeatmap();
  }

  // ---- execution-quality region: scorecards of the newest finished parents, cached per id ----
  const MAX_QUALITY_PARENTS = 8;     // bounded: eight reads at most, and only for ids not yet cached
  let scorecardCache = new Map();    // parentOrderId → TwapScorecardResult (finished parents only; immutable)
  let qualityLoading = false;
  let qualityError = null;
  // Computed from the raw list (not a `$:` value) wherever a load reads it, so it never lags a flush.
  const qualityIdsOf = (list) => newestFirst((list ?? []).filter((p) => !isWorking(p?.status))).slice(0, MAX_QUALITY_PARENTS).map((p) => p.parentOrderId);
  $: qualityIds = qualityIdsOf(parents);
  const loadQuality = createScopedLoader({
    getOrgId: () => $selectedOrgId,
    getScope: () => [strategyId],
    load: async (orgId) => {
      const missing = qualityIdsOf(parents).filter((id) => !scorecardCache.has(id));
      const pairs = await Promise.all(missing.map(async (id) => [id, (await api.getTwapParent(id, orgId))?.scorecard ?? null]));
      return pairs;
    },
    apply: (pairs) => {
      if (pairs.length > 0) {
        const next = new Map(scorecardCache);
        for (const [id, sc] of pairs) if (sc) next.set(id, sc);
        scorecardCache = next;
      }
      qualityError = null;
    },
    onError: (err) => { qualityError = errText(err); },
  });
  async function refreshQuality() {
    if (!strategyId || qualityLoading) return;
    if (qualityIdsOf(parents).every((id) => scorecardCache.has(id))) return;
    qualityLoading = true;
    try { await loadQuality(); } finally { qualityLoading = false; }
  }
  $: qualityScorecards = qualityIds.map((id) => scorecardCache.get(id)).filter(Boolean);
  $: postureMix = postureMixRollup(qualityScorecards);
  $: latency = latencyRollup(qualityScorecards);

  // ---- heatmap region: one aggregate read for this strategy's parents ----
  let heatmap = null;        // from heatmapGrid(cells); null until the first read lands
  let heatmapLoading = false;
  let heatmapError = null;
  const loadHeatmap = createScopedLoader({
    getOrgId: () => $selectedOrgId,
    getScope: () => [strategyId],
    load: (orgId) => api.getTwapHeatmap({ requestId: strategyId, source: 'strategy' }, orgId),
    apply: (res) => { heatmap = heatmapGrid(res); heatmapError = null; },
    onError: (err) => { heatmapError = errText(err); },
  });
  async function refreshHeatmap() {
    if (!strategyId || heatmapLoading) return;
    heatmapLoading = true;
    try { await loadHeatmap(); } finally { heatmapLoading = false; }
  }

  // (strategy, org) reload — the guard is seeded from raw stores/props, never a `$:` value (lesson 2026-07-14).
  let lastKey = '';
  $: {
    const key = `${strategy?.strategyId ?? ''}|${$selectedOrgId ?? ''}`;
    if (key !== lastKey && strategy?.strategyId) {
      lastKey = key;
      parents = null;
      error = null;
      confirmCancelId = null;
      scorecardCache = new Map();
      qualityError = null;
      heatmap = null;
      heatmapError = null;
      refresh();
    }
  }

  // ---- SSE hint (AC 24): debounce a burst of child/parent ticks into one authoritative refetch ----
  const HINT_DEBOUNCE_MS = 400;
  let hintTimer = null;
  function onTwapProgress(payload) {
    if (!tickTouchesStrategy(payload, strategyId)) return;
    if (hintTimer) return;
    hintTimer = setTimeout(() => { hintTimer = null; void refresh(); }, HINT_DEBOUNCE_MS);
  }
  let unsubscribeSse = null;
  onMount(() => { unsubscribeSse = addSseSubscriber({ refetchAuthoritative: () => refresh(), onTwapProgress }); });

  // ---- 30 s backstop poll while anything can still move ----
  const BACKSTOP_POLL_MS = 30000;
  let pollTimer = null;
  $: hasWorking = (parents ?? []).some((p) => isWorking(p?.status));
  $: if ((isRunning || hasWorking) && !pollTimer) {
    pollTimer = setInterval(refresh, BACKSTOP_POLL_MS);
  } else if (!isRunning && !hasWorking && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  // ---- a 1 s clock for the live cards' time %, pace and ETA (only while a parent is Working) ----
  let now = Date.now();
  let clockTimer = null;
  $: if (hasWorking && !clockTimer) {
    clockTimer = setInterval(() => { now = Date.now(); }, 1000);
  } else if (!hasWorking && clockTimer) {
    clearInterval(clockTimer);
    clockTimer = null;
  }

  onDestroy(() => {
    if (pollTimer) clearInterval(pollTimer);
    if (clockTimer) clearInterval(clockTimer);
    if (hintTimer) clearTimeout(hintTimer);
    unsubscribeSse?.();
  });

  // ---- derived views ----
  $: currency = parents?.find((p) => p?.quoteCurrency)?.quoteCurrency ?? strategy?.quoteCurrency ?? 'USD';
  $: tiles = tabKpis(parents ?? [], currency);
  $: live = liveParentsView(parents ?? [], now);
  $: symbolRows = bySymbol(parents ?? []);
  $: savingsSeries = cumulativeSavingsSeries(parents ?? []);
  $: anyPaperFees = (parents ?? []).some((p) => feeSourceBadge(p?.feeRateSource).paper);
  // ADR-0040: a parent is simulated unless its target (`exchgId` + `mode`) is real money; the badge fails
  // closed (UNKNOWN, never LIVE) for a parent the SPA cannot classify.
  const paperVenueOf = (p) => (p ? !isRealMoney(targetOf(p)) : false);
  const modeBadgeOf = (p) => targetBadge(targetOf(p));

  let statusFilter = 'all';
  let sortKey = 'startedAt';
  let sortDir = 'desc';
  $: listRows = sortKey === 'startedAt' && sortDir === 'desc'
    ? newestFirst(filterParents(parents ?? [], { status: statusFilter }))
    : sortParents(filterParents(parents ?? [], { status: statusFilter }), sortKey, sortDir);
  function sortBy(key) {
    if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    else { sortKey = key; sortDir = key === 'symbol' || key === 'status' ? 'asc' : 'desc'; }
  }
  // Reactive (not a const) so the header markers re-render when the sort changes.
  $: ariaSort = (key) => (sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none');

  // ---- report panel (the existing SlideInPanel + shared TwapReport) ----
  let reportFor = null; // { id, summary }
  function openReport(p) { reportFor = { id: p.parentOrderId, summary: p }; }
  function closeReport() { reportFor = null; }

  // ---- cancel with an explicit confirm (design-system: financial actions never silently fire) ----
  let cancelBusy = null;
  let cancelNote = {};
  async function confirmCancel(id) {
    if (cancelBusy) return;
    cancelBusy = id;
    try {
      const r = await api.cancelTwapParent(id, $selectedOrgId);
      cancelNote = { ...cancelNote, [id]: r?.accepted
        ? (r.pending ? 'Cancel requested — the owning host honours it on its next cycle.' : `Cancelled (${r.status ?? 'done'}).`)
        : `Cancel not accepted (${r?.status ?? 'unknown'}).` };
      confirmCancelId = null;
      await refresh();
    } catch (e) {
      cancelNote = { ...cancelNote, [id]: e?.message ?? 'Cancel failed' };
    } finally {
      cancelBusy = null;
    }
  }

  // ---- formatting ----
  const when = (iso) => { const d = new Date(iso ?? ''); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(); };
  const RING_R = 17;
  const RING_C = 2 * Math.PI * RING_R;
  // The pace bar spans −PACE_RANGE..+PACE_RANGE % of schedule; the ±band sits in the middle.
  const PACE_RANGE = 20;
  const paceX = (pct) => 50 + (Math.max(-PACE_RANGE, Math.min(PACE_RANGE, pct ?? 0)) / PACE_RANGE) * 50;
  const paceLabel = { on: 'on schedule', ahead: 'ahead of schedule', behind: 'behind schedule', unknown: 'pace unknown' };
</script>

<div class="tw" class:tw-live={isLive} class:tw-paper={!isLive}>
  <div class="tw-toolbar">
    <span class={`tw-mode ${isLive ? 'live' : 'paper'}`} title={isLive ? 'Real-money venue' : 'Paper / sandbox venue — simulated fills'}>{isLive ? 'LIVE' : 'PAPER'}</span>
    {#if hasWorking}<span class="tw-livedot" title="Updating on every TWAP progress event">● live</span>{/if}
    <div class="tw-filters" role="group" aria-label="Filter parents by status">
      {#each STATUS_FILTERS as f (f.key)}
        <button type="button" class="tw-chip" class:active={statusFilter === f.key} aria-pressed={statusFilter === f.key}
          on:click={() => (statusFilter = f.key)}>{f.label}</button>
      {/each}
    </div>
    <button type="button" class="btn btn-ghost btn-sm" on:click={refresh} disabled={inFlight}>Refresh</button>
  </div>

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={refresh}>Retry</button>
    </div>
  {/if}

  {#if loading && parents === null}
    <div class="at-skeleton-rows" aria-busy="true"><div class="at-skeleton-row"></div><div class="at-skeleton-row"></div></div>
  {:else if parents !== null && parents.length === 0}
    <div class="empty-state">
      <p class="empty-state-title">No TWAP executions for this strategy yet</p>
      <p class="empty-state-description">
        Parents appear here as the strategy's intents execute under a TWAP policy.
        {#if isRunning}This tab updates live as slices fill.{/if}
      </p>
    </div>
  {:else if parents !== null}
    <!-- KPI tiles: client rollups over the list (server summary rollups are a v1 non-goal). -->
    <section class="tw-tiles" aria-label="TWAP savings summary">
      {#each tiles as t (t.key)}
        <div class={`tw-tile tw-tone-${t.tone}`} title={t.hint ?? ''}>
          <span class="tw-tile-label">{t.label}</span>
          <span class="tw-tile-value num">{t.text}</span>
          {#if t.sub}<span class="tw-tile-sub num">{t.sub}</span>{/if}
        </div>
      {/each}
    </section>
    {#if anyPaperFees}
      <p class="tw-paper-note" role="note">⚠ Paper fee schedule in effect on some or all parents — fee savings are simulated at the paper rates, not live venue fees.</p>
    {/if}

    <!-- Live parent cards: one per Working parent. -->
    {#if live.length > 0}
      <section class="tw-live" aria-label="Working TWAP parents">
        <h4 class="tw-panel-title">Working now ({live.length})</h4>
        <div class="tw-cards">
          {#each live as v (v.id)}
            <article class="tw-card" class:tw-card-paper={paperVenueOf(v.summary)} aria-label={`${v.side} ${v.symbol} TWAP parent, ${v.progressPct != null ? Math.round(v.progressPct) : 0} % filled, ${paceLabel[v.paceState]}`}>
              <div class="tw-card-head">
                <span class={`tw-side tw-side-${String(v.side).toLowerCase()}`}>{v.side}</span>
                <strong class="tw-symbol">{v.symbol}</strong>
                <span class={`tw-status tw-status-${statusTone(v.status)}`}>{STATUS_LABELS[v.status] ?? v.status}</span>
                {#if v.cancelRequested}<span class="tw-status tw-status-cancelled">cancel requested</span>{/if}
                <span class={`tw-mode tw-mode-sm ${paperVenueOf(v.summary) ? 'paper' : 'live'}`}>{modeBadgeOf(v.summary)}</span>
              </div>
              <div class="tw-card-body">
                <!-- Progress ring: filled share of the target. -->
                <svg class="tw-ring" viewBox="0 0 44 44" width="64" height="64" role="img" aria-label={`Progress ${v.progressPct != null ? v.progressPct.toFixed(1) : '—'} %`}>
                  <circle class="tw-ring-track" cx="22" cy="22" r={RING_R} />
                  <circle class="tw-ring-fill" cx="22" cy="22" r={RING_R}
                    stroke-dasharray={RING_C} stroke-dashoffset={RING_C * (1 - (v.progressPct ?? 0) / 100)} />
                  <text class="tw-ring-text" x="22" y="22" text-anchor="middle" dominant-baseline="central">{v.progressPct != null ? Math.round(v.progressPct) : '—'}%</text>
                </svg>
                <div class="tw-card-stats">
                  <div class="tw-stat"><span class="tw-stat-label">Filled</span>
                    <span class="num">{fmtQty(v.summary.filledQty)}{#if v.summary.targetQty != null} / {fmtQty(v.summary.targetQty)}{/if}
                      {#if v.summary.targetAmount != null} ({formatQuoteAmount(v.summary.filledAmount, currency)} / {formatQuoteAmount(v.summary.targetAmount, currency)}){/if}</span></div>
                  <div class="tw-stat"><span class="tw-stat-label">Cycles</span><span class="num">{v.cyclesText}</span></div>
                  <div class="tw-stat"><span class="tw-stat-label">Time</span><span class="num">{v.timePct != null ? `${v.timePct.toFixed(0)} %` : '—'} · ETA {v.etaText}</span></div>
                  <div class="tw-stat"><span class="tw-stat-label">Posture</span>
                    <span class="tw-posture">{v.postureLabel ?? '—'}</span>
                    <span class="tw-sub num" title="Pace adherence: 1 − mean |pace-band offset| over the slices so far">adherence {v.paceAdherenceText}</span></div>
                  <!-- Pace bar: pace = filled % − time %, the ±band is on-schedule. Sign + arrow + label, never color-only. -->
                  <div class="tw-pace" role="img" aria-label={`Pace ${v.paceText}, ${paceLabel[v.paceState]}`}>
                    <span class="tw-stat-label">Pace</span>
                    <svg class="tw-pace-bar" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
                      <rect class="tw-pace-track" x="0" y="3" width="100" height="4" rx="2" />
                      <rect class="tw-pace-band" x={paceX(-PACE_BAND_PCT)} y="2" width={paceX(PACE_BAND_PCT) - paceX(-PACE_BAND_PCT)} height="6" rx="1" />
                      <line class="tw-pace-zero" x1="50" y1="0" x2="50" y2="10" />
                      {#if v.pacePct != null}
                        <rect class={`tw-pace-marker tw-pace-${v.paceState}`} x={paceX(v.pacePct) - 1.2} y="0" width="2.4" height="10" rx="1" />
                      {/if}
                    </svg>
                    <span class={`tw-pace-text num tw-pace-${v.paceState}`}>{v.paceText} · {paceLabel[v.paceState]}</span>
                  </div>
                </div>
              </div>
              <div class="tw-card-actions">
                <button type="button" class="btn btn-ghost btn-sm" on:click={() => openReport(v.summary)}>Report</button>
                {#if !v.cancelRequested}
                  {#if confirmCancelId === v.id}
                    <span class="tw-confirm" role="group" aria-label="Confirm cancel">
                      <span class="tw-confirm-text">Cancel this parent? Filled slices stay filled.</span>
                      <button type="button" class="btn btn-danger btn-sm" disabled={cancelBusy === v.id} on:click={() => confirmCancel(v.id)}>{cancelBusy === v.id ? 'Cancelling…' : 'Confirm cancel'}</button>
                      <button type="button" class="btn btn-ghost btn-sm" on:click={() => (confirmCancelId = null)}>Keep</button>
                    </span>
                  {:else}
                    <button type="button" class="btn btn-ghost btn-sm tw-cancel" on:click={() => (confirmCancelId = v.id)}>Cancel</button>
                  {/if}
                {/if}
              </div>
              {#if cancelNote[v.id]}<div class="tw-note" role="status">{cancelNote[v.id]}</div>{/if}
            </article>
          {/each}
        </div>
      </section>
    {/if}

    <!-- PR-C2: cumulative savings over time (pure, from the list — no extra read). -->
    <section class="tw-panel" aria-label="Cumulative net savings">
      <h4 class="tw-panel-title">Savings over time <span class="tw-sub">net vs sweep, by parent end time</span></h4>
      <CumulativeSavingsChart series={savingsSeries} {currency} />
    </section>

    <!-- PR-C2: execution quality — its own read (newest finished scorecards), its own states. -->
    <section class="tw-panel" aria-label="Execution quality">
      <h4 class="tw-panel-title">Execution quality <span class="tw-sub">newest {Math.min(MAX_QUALITY_PARENTS, qualityIds.length)} finished parent{qualityIds.length === 1 ? '' : 's'}</span></h4>
      {#if qualityError}
        <div class="alert alert-danger" role="alert">{qualityError}
          <button type="button" class="btn btn-ghost btn-sm" on:click={refreshQuality}>Retry</button>
        </div>
      {:else if qualityIds.length === 0}
        <p class="tw-note">Posture mix and slice latency appear once a parent finishes.</p>
      {:else if qualityLoading && qualityScorecards.length === 0}
        <div class="at-skeleton-rows" aria-busy="true"><div class="at-skeleton-row"></div></div>
      {:else}
        <div class="tw-quality">
          <PostureMixBar mix={postureMix} emptyText="No filled slices in the newest finished parents." />
          <LatencyTile p50Ms={latency.p50Ms} p95Ms={latency.p95Ms} loading={qualityLoading}
            sub={latency.parents > 0 ? `weighted median p50 / worst p95 over ${latency.parents} parent${latency.parents === 1 ? '' : 's'}, ${latency.children} slices` : 'no resolved slices reported yet'} />
        </div>
      {/if}
    </section>

    <!-- PR-C2: maker-capture heatmap — one aggregate read for this strategy, independent states. -->
    <section class="tw-panel" aria-label="Maker capture heatmap">
      <MakerCaptureHeatmap grid={heatmap} loading={heatmapLoading} error={heatmapError}
        emptyText="No fills to map yet — the heatmap fills in as slices execute." />
      {#if heatmapError}<button type="button" class="btn btn-ghost btn-sm" on:click={refreshHeatmap}>Retry</button>{/if}
    </section>

    <!-- Parent list: newest first by default; sortable; each row opens the shared report. -->
    <section class="tw-list" aria-label="TWAP parents">
      <h4 class="tw-panel-title">Executions ({listRows.length}{listRows.length !== parents.length ? ` of ${parents.length}` : ''})</h4>
      {#if listRows.length === 0}
        <p class="tw-note">No parents match this filter.</p>
      {:else}
        <div class="tw-scroll">
          <table>
            <thead>
              <tr>
                <th aria-sort={ariaSort('startedAt')}><button type="button" class="tw-sort" on:click={() => sortBy('startedAt')}>Started</button></th>
                <th aria-sort={ariaSort('symbol')}><button type="button" class="tw-sort" on:click={() => sortBy('symbol')}>Symbol</button></th>
                <th>Side</th>
                <th aria-sort={ariaSort('status')}><button type="button" class="tw-sort" on:click={() => sortBy('status')}>Status</button></th>
                <th class="num" aria-sort={ariaSort('filledAmount')}><button type="button" class="tw-sort" on:click={() => sortBy('filledAmount')}>Filled</button></th>
                <th class="num" aria-sort={ariaSort('netSavingsQuote')}><button type="button" class="tw-sort" on:click={() => sortBy('netSavingsQuote')}>Net savings</button></th>
                <th class="num" aria-sort={ariaSort('makerFillPct')}><button type="button" class="tw-sort" on:click={() => sortBy('makerFillPct')}>Maker fill</button></th>
                <th>Fees</th>
                <th><span class="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {#each listRows as p (p.parentOrderId)}
                {@const fee = feeSourceBadge(p.feeRateSource)}
                <tr class:tw-row-working={isWorking(p.status)}>
                  <td class="num">{when(p.startedAt)}</td>
                  <td>{p.symbol}</td>
                  <td class={`tw-side tw-side-${String(p.side).toLowerCase()}`}>{p.side}</td>
                  <td><span class={`tw-status tw-status-${statusTone(p.status)}`}>{STATUS_LABELS[p.status] ?? p.status}</span></td>
                  <td class="num">{fmtQty(p.filledQty)}{#if p.targetQty != null} / {fmtQty(p.targetQty)}{/if}<br /><span class="tw-sub">{formatQuoteAmount(p.filledAmount, p.quoteCurrency ?? currency)}</span></td>
                  <td class={`num tw-tone-${p.netSavingsQuote > 0 ? 'gain' : p.netSavingsQuote < 0 ? 'loss' : 'neutral'}`}>{fmtQuote(p.netSavingsQuote, p.quoteCurrency ?? currency)}<br /><span class="tw-sub">{fmtPct(p.netSavingsPct)} · {fmtBps(p.improvementVsSweepBps)}</span></td>
                  <td class="num">{fmtPct(p.makerFillPct, { signed: false, decimals: 1 })}</td>
                  <td><span class="tw-fee" class:tw-fee-paper={fee.paper} title={p.feeRateSource ?? ''}>{fee.paper ? '⚠ ' : ''}{fee.label}</span></td>
                  <td><button type="button" class="btn btn-ghost btn-sm" on:click={() => openReport(p)} aria-label={`Open report for ${p.side} ${p.symbol} started ${when(p.startedAt)}`}>Report</button></td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>

    <!-- Savings by symbol. -->
    {#if symbolRows.length > 0}
      <section class="tw-symbols" aria-label="Savings by symbol">
        <h4 class="tw-panel-title">Savings by symbol</h4>
        <div class="tw-scroll">
          <table>
            <thead><tr><th>Symbol</th><th class="num">Parents</th><th class="num">Filled notional</th><th class="num">Net savings</th><th class="num">Net %</th><th class="num">vs sweep</th><th class="num">Maker fill</th></tr></thead>
            <tbody>
              {#each symbolRows as r (r.key)}
                <tr>
                  <td>{r.key}</td>
                  <td class="num">{r.count}</td>
                  <td class="num">{formatQuoteAmount(r.notional, currency)}</td>
                  <td class={`num tw-tone-${r.netSavingsQuote > 0 ? 'gain' : r.netSavingsQuote < 0 ? 'loss' : 'neutral'}`}>{fmtQuote(r.netSavingsQuote, currency)}</td>
                  <td class="num">{fmtPct(r.netSavingsPct)}</td>
                  <td class="num">{fmtBps(r.improvementVsSweepBps)}</td>
                  <td class="num">{fmtPct(r.makerFillPct, { signed: false, decimals: 1 })}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}
  {/if}
</div>

<SlideInPanel open={reportFor !== null} title={reportFor ? `TWAP report · ${reportFor.summary?.symbol ?? ''}` : 'TWAP report'} on:close={closeReport}>
  {#if reportFor}
    <TwapReport parentId={reportFor.id} orgId={$selectedOrgId} initial={reportFor.summary} />
  {/if}
</SlideInPanel>

<style>
  .tw { display: flex; flex-direction: column; gap: 0.85rem; }
  .tw-toolbar { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
  .tw-livedot { font-size: var(--font-size-xs, 0.75rem); color: var(--color-success, #1aa260); }
  .tw-panel-title { margin: 0 0 0.5rem; font-size: var(--font-size-sm, 0.8rem); color: var(--text-secondary, #b5b6b8); }
  .num { font-variant-numeric: tabular-nums; }
  .tw-sub { font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #8a8b8d); }
  .tw-note { font-size: var(--font-size-xs, 0.72rem); color: var(--text-secondary, #b5b6b8); margin: 0; }
  .visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

  /* Live vs paper — unmistakable: the mode badge (text, not color alone) plus a border accent. */
  .tw-mode {
    font-size: var(--font-size-xs, 0.65rem); font-weight: var(--font-weight-bold, 700); letter-spacing: 0.06em;
    padding: 0.15rem 0.45rem; border-radius: var(--radius-sm, 4px);
    background: var(--bg-tertiary, rgba(120, 160, 255, 0.12)); color: var(--text-secondary, #b5b6b8);
  }
  .tw-mode.live { background: var(--color-danger-alpha-20, rgba(240, 90, 70, 0.18)); color: var(--color-danger, #f0594a); }
  .tw-mode-sm { margin-left: auto; }
  .tw-paper-note { margin: 0; font-size: var(--font-size-xs, 0.72rem); color: var(--color-warning, #d8a000); }

  .tw-filters { display: inline-flex; gap: 0.25rem; }
  .tw-chip {
    appearance: none; cursor: pointer; font-size: var(--font-size-xs, 0.72rem);
    padding: 0.2rem 0.6rem; border-radius: var(--radius-full, 999px);
    border: 1px solid var(--border-secondary, #2a2b2e); background: transparent; color: var(--text-secondary, #b5b6b8);
  }
  .tw-chip.active { border-color: var(--color-primary, #3c78f0); color: var(--text-primary, #e8e9ea); background: var(--color-primary-alpha-10, rgba(60, 120, 240, 0.1)); }
  .tw-chip:focus-visible, .tw-sort:focus-visible, .tw-cancel:focus-visible { outline: 2px solid var(--color-primary, #3c78f0); outline-offset: 1px; }

  /* KPI tiles */
  .tw-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 0.6rem; }
  .tw-tile {
    display: flex; flex-direction: column; gap: 0.15rem;
    border: 1px solid var(--border-secondary, #1e1f22); border-radius: var(--radius-md, 8px); padding: 0.6rem 0.75rem;
    background: var(--bg-secondary, rgba(255, 255, 255, 0.02));
  }
  .tw-tile-label { font-size: var(--font-size-xs, 0.62rem); text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted, #8a8b8d); }
  .tw-tile-value { font-size: var(--font-size-lg, 1.05rem); font-weight: var(--font-weight-bold, 700); color: var(--text-primary, #e8e9ea); }
  .tw-tile-sub { font-size: var(--font-size-xs, 0.68rem); color: var(--text-muted, #8a8b8d); }
  .tw-tone-gain .tw-tile-value, td.tw-tone-gain { color: var(--color-success, #1aa260); }
  .tw-tone-loss .tw-tile-value, td.tw-tone-loss { color: var(--color-danger, #e0533a); }

  /* Live cards */
  .tw-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 0.6rem; }
  .tw-card {
    display: flex; flex-direction: column; gap: 0.5rem;
    border: 1px solid var(--color-warning, #d8a000); border-left-width: 3px; border-radius: var(--radius-md, 8px);
    padding: 0.6rem 0.75rem; background: var(--bg-secondary, rgba(255, 255, 255, 0.02));
  }
  .tw-card-head { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .tw-symbol { color: var(--text-primary, #e8e9ea); }
  .tw-side { font-weight: var(--font-weight-bold, 700); text-transform: uppercase; font-size: var(--font-size-xs, 0.72rem); }
  .tw-side-buy { color: var(--color-success, #1aa260); }
  .tw-side-sell { color: var(--color-danger, #e0533a); }
  .tw-card-body { display: grid; grid-template-columns: 64px minmax(0, 1fr); gap: 0.75rem; align-items: start; }
  .tw-ring-track { fill: none; stroke: var(--bg-quaternary, #2a2b2e); stroke-width: 4; }
  .tw-ring-fill {
    fill: none; stroke: var(--color-primary, #3c78f0); stroke-width: 4; stroke-linecap: round;
    transform: rotate(-90deg); transform-origin: 50% 50%; transition: stroke-dashoffset var(--transition-base, 0.25s ease);
  }
  .tw-ring-text { fill: var(--text-primary, #e8e9ea); font-size: 10px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .tw-card-stats { display: flex; flex-direction: column; gap: 0.25rem; font-size: var(--font-size-xs, 0.75rem); color: var(--text-primary, #e8e9ea); min-width: 0; }
  .tw-stat { display: flex; gap: 0.5rem; align-items: baseline; }
  .tw-stat-label { min-width: 3.4rem; font-size: var(--font-size-xs, 0.62rem); text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted, #8a8b8d); }
  .tw-posture { border: 1px solid var(--border-secondary, #2a2b2e); border-radius: var(--radius-full, 999px); padding: 0 0.45rem; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #b5b6b8); }
  .tw-pace { display: grid; grid-template-columns: 3.4rem minmax(0, 1fr); gap: 0.5rem 0.5rem; align-items: center; }
  .tw-pace-bar { width: 100%; height: 12px; display: block; }
  .tw-pace-track { fill: var(--bg-quaternary, #2a2b2e); }
  .tw-pace-band { fill: var(--color-success-alpha-20, rgba(26, 162, 96, 0.2)); }
  .tw-pace-zero { stroke: var(--text-muted, #8a8b8d); stroke-width: 0.6; }
  .tw-pace-marker { fill: var(--color-primary, #3c78f0); transition: x var(--transition-base, 0.25s ease); }
  .tw-pace-marker.tw-pace-on { fill: var(--color-success, #1aa260); }
  .tw-pace-marker.tw-pace-behind { fill: var(--color-danger, #e0533a); }
  .tw-pace-marker.tw-pace-ahead { fill: var(--color-warning, #d8a000); }
  .tw-pace-text { grid-column: 2; }
  .tw-pace-text.tw-pace-on { color: var(--color-success, #1aa260); }
  .tw-pace-text.tw-pace-behind { color: var(--color-danger, #e0533a); }
  .tw-pace-text.tw-pace-ahead { color: var(--color-warning, #d8a000); }
  .tw-card-actions { display: flex; gap: 0.4rem; align-items: center; flex-wrap: wrap; }
  .tw-confirm { display: inline-flex; gap: 0.35rem; align-items: center; flex-wrap: wrap; }
  .tw-confirm-text { font-size: var(--font-size-xs, 0.72rem); color: var(--text-secondary, #b5b6b8); }
  .tw-cancel { color: var(--color-danger, #e0533a); }

  /* Status chips (mirrors the Orders blotter tones) */
  .tw-status { font-size: var(--font-size-xs, 0.65rem); text-transform: uppercase; letter-spacing: 0.04em; padding: 0.1rem 0.4rem; border-radius: var(--radius-sm, 4px); white-space: nowrap; }
  .tw-status-working { color: var(--color-warning, #d8a000); border: 1px solid var(--color-warning, #d8a000); }
  .tw-status-done { color: var(--color-success, #1aa260); border: 1px solid var(--color-success, #1aa260); }
  .tw-status-partial { color: var(--color-primary, #3c78f0); border: 1px solid var(--color-primary, #3c78f0); }
  .tw-status-cancelled { color: var(--text-secondary, #b5b6b8); border: 1px solid var(--border-secondary, #2a2b2e); }
  .tw-status-failed { color: var(--color-danger, #e0533a); border: 1px solid var(--color-danger, #e0533a); }
  .tw-status-neutral { color: var(--text-secondary, #b5b6b8); border: 1px solid var(--border-secondary, #2a2b2e); }
  .tw-fee { font-size: var(--font-size-xs, 0.68rem); color: var(--text-secondary, #b5b6b8); white-space: nowrap; }
  .tw-fee-paper { color: var(--color-warning, #d8a000); }

  /* Panels (PR-C2 regions) */
  .tw-panel { border: 1px solid var(--border-secondary, #1e1f22); border-radius: var(--radius-md, 8px); padding: 0.75rem; min-width: 0; }
  .tw-quality { display: grid; grid-template-columns: minmax(0, 2fr) minmax(200px, 1fr); gap: 0.75rem; align-items: start; }
  @media (max-width: 640px) { .tw-quality { grid-template-columns: 1fr; } }

  /* Tables */
  .tw-list, .tw-symbols { border: 1px solid var(--border-secondary, #1e1f22); border-radius: var(--radius-md, 8px); padding: 0.75rem; }
  .tw-scroll { max-height: 420px; overflow: auto; }
  table { width: 100%; border-collapse: collapse; font-size: var(--font-size-xs, 0.75rem); }
  th, td { padding: 0.32rem 0.5rem; border-bottom: 1px solid var(--border-secondary, #1e1f22); white-space: nowrap; text-align: left; vertical-align: top; }
  th { position: sticky; top: 0; background: var(--bg-secondary, #14151a); color: var(--text-secondary, #b5b6b8); font-weight: var(--font-weight-semibold, 600); }
  th.num, td.num { text-align: right; }
  .tw-sort { appearance: none; background: none; border: none; padding: 0; font: inherit; color: inherit; cursor: pointer; }
  th[aria-sort="ascending"] .tw-sort::after { content: ' ▲'; }
  th[aria-sort="descending"] .tw-sort::after { content: ' ▼'; }
  .tw-row-working td { background: var(--color-warning-alpha-20, rgba(216, 160, 0, 0.08)); }

  @media (prefers-reduced-motion: reduce) {
    .tw-ring-fill, .tw-pace-marker { transition: none; }
  }
</style>
